import { useState } from "react";
import { MoreHorizontal, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useHaptics } from "@/hooks/use-haptics";
import { useRecentTools } from "@/hooks/use-recent-tools";
import { primaryNav, secondaryNav, tradesMenu, tradesIcon, type NavItem } from "@/lib/nav-items";
import MobileNavDrawer from "@/components/MobileNavDrawer";

const [homeTab, jobsTab, shopTab] = primaryNav;
const TradesIcon = tradesIcon;

const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { lightTap } = useHaptics();
  const { trackVisit } = useRecentTools();
  const [menu, setMenu] = useState<null | "trades" | "more">(null);

  const pathOf = (item: NavItem) => item.path.split("?")[0];
  const isTradeActive = tradesMenu.some((t) => t.path === location.pathname);
  const topLevel = new Set([...primaryNav, ...tradesMenu].map(pathOf));
  const isMoreActive =
    secondaryNav.some((t) => pathOf(t) === location.pathname) && !topLevel.has(location.pathname);

  const open = (item: NavItem) => {
    lightTap();
    trackVisit(pathOf(item), item.label);
    navigate(item.path);
    setMenu(null);
  };

  const menuItems = tradesMenu;

  const Slot = ({
    label,
    icon: Icon,
    active,
    onClick,
    expanded,
  }: {
    label: string;
    icon: NavItem["icon"];
    active: boolean;
    onClick: () => void;
    expanded?: boolean;
  }) => (
    <button
      onClick={onClick}
      aria-current={active && expanded === undefined ? "page" : undefined}
      aria-expanded={expanded}
      aria-label={label}
      className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors ${
        active ? "text-primary" : "text-muted-foreground"
      }`}
    >
      <Icon className="w-5 h-5" />
      <span className="text-[10px] font-semibold uppercase tracking-wider leading-none">{label}</span>
    </button>
  );

  return (
    <>
      <MobileNavDrawer open={menu === "more"} onClose={() => setMenu(null)} />

      {menu === "trades" && (
        <div className="fixed inset-0 z-[60] md:hidden" onClick={() => setMenu(null)}>
          <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
          <div
            className="absolute bottom-[calc(3.5rem+env(safe-area-inset-bottom))] left-2 right-2 bg-card border border-border rounded-xl shadow-lg p-2 animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-3 py-1.5 mb-1">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Trades
              </span>
              <button onClick={() => setMenu(null)} className="p-1 rounded-md hover:bg-secondary text-muted-foreground" aria-label="Close menu">
                <X className="w-4 h-4" />
              </button>
            </div>
            {menuItems.map((tab) => {
              const isActive = location.pathname === pathOf(tab);
              return (
                <button
                  key={tab.path}
                  onClick={() => open(tab)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive ? "bg-primary/10 text-primary" : "text-foreground hover:bg-secondary"
                  }`}
                >
                  <tab.icon className="w-5 h-5 shrink-0" />
                  <div className="text-left">
                    <div className="text-sm font-medium">{tab.label}</div>
                    <div className="text-[11px] text-muted-foreground">{tab.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <nav
        className="fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50 md:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        aria-label="Main navigation"
      >
        <div className="flex items-center justify-around h-14" role="tablist">
          <Slot label={homeTab.label} icon={homeTab.icon} active={location.pathname === "/"} onClick={() => open(homeTab)} />
          <Slot
            label="Trades"
            icon={TradesIcon}
            active={isTradeActive || menu === "trades"}
            expanded={menu === "trades"}
            onClick={() => { lightTap(); setMenu((m) => (m === "trades" ? null : "trades")); }}
          />
          <Slot label={jobsTab.label} icon={jobsTab.icon} active={location.pathname === "/jobs"} onClick={() => open(jobsTab)} />
          <Slot label={shopTab.label} icon={shopTab.icon} active={location.pathname === "/shop"} onClick={() => open(shopTab)} />
          <Slot
            label="More"
            icon={MoreHorizontal}
            active={isMoreActive || menu === "more"}
            expanded={menu === "more"}
            onClick={() => { lightTap(); setMenu((m) => (m === "more" ? null : "more")); }}
          />
        </div>
      </nav>
    </>
  );
};

export default BottomNav;
